import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { TechnoCursor } from "@/components/TechnoCursor";
import { VinciShieldTransition } from "@/components/VinciShieldTransition";
import { usePulseTracker } from "@/hooks/usePulseTracker";
import { DailyPulseGate } from "@/components/DailyPulseGate";
import { IdleLockGate } from "@/components/IdleLockGate";
import { CartSheet } from "@/components/CartSheet";
import { ConditionsAckBanner } from "@/components/ConditionsAckBanner";
import { RegistrationDeadlineBanner } from "@/components/RegistrationDeadlineBanner";
import { ProtectedRoute, ActiveOnlyRoute, DirectorRoute } from "@/components/ProtectedRoute";
import DirectorApply from "@/pages/DirectorApply";
import DirectorPortal from "@/pages/DirectorPortal";
import BoardRegistryPage from "@/pages/BoardRegistryPage";
import SupplierLicense from "@/pages/SupplierLicense";
import Home from "@/pages/Home";
import Admin from "@/pages/Admin";
import Account from "@/pages/Account";
import AccountDocuments from "@/pages/AccountDocuments";
import AccountDeals from "@/pages/AccountDeals";
import EcosystemRegistry from "@/pages/EcosystemRegistry";
import VerifyReceipt from "@/pages/VerifyReceipt";
import DirectorKYC from "@/pages/DirectorKYC";
import SupplierDashboard from "@/pages/SupplierDashboard";
import SellerEliteApply from "@/pages/SellerEliteApply";
import BigCapLeague from "@/pages/BigCapLeague";
import BigCapApply from "@/pages/BigCapApply";
import AccountStatement from "@/pages/AccountStatement";
import AccountInvoices from "@/pages/AccountInvoices";
import BillDesk from "@/pages/BillDesk";
import RatesBulletin from "@/pages/RatesBulletin";
import AccountMeetings from "@/pages/AccountMeetings";
import Onboarding from "@/pages/Onboarding";
import OnboardingVerify from "@/pages/OnboardingVerify";
import PaidQuotes from "@/pages/PaidQuotes";
import SupplierSettlement from "@/pages/SupplierSettlement";
import SupplierBroadcasts from "@/pages/SupplierBroadcasts";
import BuyerClaims from "@/pages/BuyerClaims";
import RefundComplianceDesk from "@/pages/RefundComplianceDesk";
import SellerQuotations from "@/pages/SellerQuotations";
import WalletPage from "@/pages/Wallet";
import WalletActivate from "@/pages/WalletActivate";
import Careers from "@/pages/Careers";
import ExecutiveCommand from "@/pages/ExecutiveCommand";
import { Login, ForgotPassword, ResetPassword } from "@/pages/Auth";
import RegisterHub from "@/pages/RegisterHub";
import BuyerRegister from "@/pages/BuyerRegister";
import SellerRegister from "@/pages/SellerRegister";
import PulseVerification from "@/pages/PulseVerification";
import PulseAudit from "@/pages/PulseAudit";
import { VirtualExecutive } from "@/components/VirtualExecutive";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import Privacy from "@/pages/Privacy";
import Accessibility from "@/pages/Accessibility";
import Terms from "@/pages/Terms";
import Refund from "@/pages/Refund";
import Delivery from "@/pages/Delivery";
import RequestQuote from "@/pages/RequestQuote";
import MarketTrends from "@/pages/MarketTrends";
import PartnerNetwork from "@/pages/PartnerNetwork";
import LeadershipPage from "@/pages/LeadershipPage";
import AdminBureaucraticLibrary from "@/pages/AdminBureaucraticLibrary";
import AdminCorrectionPool from "@/pages/AdminCorrectionPool";
import CorrectionSubmit from "@/pages/CorrectionSubmit";
import KycVerify from "@/pages/KycVerify";
import VerificationPending from "@/pages/VerificationPending";
import AccessSuspended from "@/pages/AccessSuspended";
import PayReturn from "@/pages/PayReturn";
import HostedPay from "@/pages/HostedPay";
import InvoicePayRedirect from "@/pages/InvoicePayRedirect";
import BrandsIndex from "@/pages/BrandsIndex";
import VCBInduction from "@/pages/VCBInduction";
import VCBMappings, { VCBMapAccept } from "@/pages/VCBMappings";
import VCBTerminal from "@/pages/VCBTerminal";
import Manual from "@/pages/Manual";
import VCBPremium from "@/pages/VCBPremium";
import PDLayout from "@/property-desk/PDLayout";
import PDLanding from "@/property-desk/pages/Landing";
import PDDiscover from "@/property-desk/pages/Discover";
import PDDetail from "@/property-desk/pages/Detail";
import { InsightsList as PDInsights, InsightArticle as PDInsightArticle } from "@/property-desk/pages/Insights";
import PDRequirements from "@/property-desk/pages/Requirements";
import PDSubmit from "@/property-desk/pages/Submit";
import PDPartner from "@/property-desk/pages/Partner";
import { CitiesIndex as PDCities, CityPage as PDCity } from "@/property-desk/pages/Cities";
import PDInterests from "@/property-desk/pages/Interests";
import { CookieBanner } from "@/components/CookieBanner";
import { AdminRouteShell } from "./components/admin/AdminShell";
import ClaimForm from "./pages/ClaimForm";

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, lerp: 0.09 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
}

function _brandCategoryFromPath(path) {
  // Best-effort brand + category inference from URL. Product / catalog
  // pages pass their own explicit context via `trackBrandView` later.
  const p = (path || "").toLowerCase();
  let category = "";
  if (p.includes("/steel") || p.includes("/tmt")) category = "steel";
  else if (p.includes("/cement")) category = "cement";
  else if (p.includes("/aac") || p.includes("/block")) category = "blocks";
  else if (p.includes("/brand")) category = "brands";
  else if (p.includes("/shop")) category = "shop";
  const brand = "";  // set explicitly by /shop/:id and brand pages
  return { brand, category };
}

function PulseTrackerBoot() {
  const bc = _brandCategoryFromPath(window.location.pathname);
  usePulseTracker(bc);
  return null;
}

function App() {
  useLenis();
  return (
    <div className="App grain">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <TechnoCursor />
            <VinciShieldTransition />
            <PulseTrackerBoot />
            <CartSheet />
            <CookieBanner />
            <ConditionsAckBanner />
            <RegistrationDeadlineBanner />
            <DailyPulseGate />
            <IdleLockGate />
            <VirtualExecutive />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/register" element={<RegisterHub />} />
              <Route path="/register/buyer" element={<BuyerRegister />} />
              <Route path="/register/seller" element={<SellerRegister />} />
              <Route path="/pulse-verification/:token" element={<PulseVerification />} />
              <Route path="/vcb/induct/:token" element={<VCBInduction />} />
              <Route path="/vcb/mappings" element={<VCBMappings />} />
              <Route path="/vcb/map/:token" element={<VCBMapAccept />} />
              <Route path="/vcb" element={<ProtectedRoute><VCBPremium /></ProtectedRoute>} />
              <Route path="/property-desk" element={<PDLayout />}>
                <Route index element={<PDLanding />} />
                <Route path="explore" element={<PDDiscover />} />
                <Route path="curated" element={<PDDiscover curatedOnly />} />
                <Route path="p/:slug" element={<PDDetail />} />
                <Route path="s/:token" element={<PDDetail />} />
                <Route path="cities" element={<PDCities />} />
                <Route path="city/:slug" element={<PDCity />} />
                <Route path="interests" element={<ProtectedRoute><PDInterests /></ProtectedRoute>} />
                <Route path="insights" element={<PDInsights />} />
                <Route path="insights/:slug" element={<PDInsightArticle />} />
                <Route path="requirements" element={<PDRequirements />} />
                <Route path="submit" element={<ProtectedRoute><PDSubmit /></ProtectedRoute>} />
                <Route path="partner" element={<ProtectedRoute><PDPartner /></ProtectedRoute>} />
              </Route>
              <Route path="/vcb-terminal" element={<ProtectedRoute><VCBTerminal /></ProtectedRoute>} />
              <Route path="/admin/manual" element={<ProtectedRoute adminOnly><Manual /></ProtectedRoute>} />
              <Route path="/admin/manual/:sectionId" element={<ProtectedRoute adminOnly><Manual /></ProtectedRoute>} />
              <Route path="/audit/:token" element={<PulseAudit />} />
              <Route path="/shop" element={<ActiveOnlyRoute><Shop /></ActiveOnlyRoute>} />
              <Route path="/shop/:id" element={<ActiveOnlyRoute><ProductDetail /></ActiveOnlyRoute>} />
              <Route path="/request-quote" element={<RequestQuote />} />
              <Route path="/trends" element={<MarketTrends />} />
              <Route path="/brands" element={<ActiveOnlyRoute><BrandsIndex /></ActiveOnlyRoute>} />
              <Route path="/network" element={<ActiveOnlyRoute><PartnerNetwork /></ActiveOnlyRoute>} />
              <Route path="/leadership" element={<LeadershipPage />} />
              <Route path="/admin/bureaucratic" element={<ProtectedRoute><AdminRouteShell><AdminBureaucraticLibrary /></AdminRouteShell></ProtectedRoute>} />
              <Route path="/admin/corrections" element={<ProtectedRoute><AdminRouteShell><AdminCorrectionPool /></AdminRouteShell></ProtectedRoute>} />
              <Route path="/corrections/submit/:slug" element={<ProtectedRoute><CorrectionSubmit /></ProtectedRoute>} />
              <Route path="/corrections/fill/:token" element={<ProtectedRoute><CorrectionSubmit /></ProtectedRoute>} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/admin/command" element={<ProtectedRoute><ExecutiveCommand /></ProtectedRoute>} />
              <Route path="/executive-command" element={<ProtectedRoute><ExecutiveCommand /></ProtectedRoute>} />
              <Route path="/checkout" element={<ActiveOnlyRoute><Checkout /></ActiveOnlyRoute>} />
              <Route path="/order-success" element={<OrderSuccess />} />
              <Route path="/verify" element={<EcosystemRegistry />} />
              <Route path="/verify-receipt" element={<VerifyReceipt />} />
              <Route path="/registry" element={<EcosystemRegistry />} />
              <Route path="/verification-pending" element={<VerificationPending />} />
              <Route path="/access-suspended" element={<AccessSuspended />} />
              <Route path="/pay/return" element={<PayReturn />} />
              <Route path="/pay/invoice/:iid" element={<InvoicePayRedirect />} />
              <Route path="/pay/certificate/:iid" element={<InvoicePayRedirect kind="certificate" />} />
              <Route path="/pay/:intentId" element={<HostedPay />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/accessibility" element={<Accessibility />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/delivery" element={<Delivery />} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/account/documents" element={<ProtectedRoute><AccountDocuments /></ProtectedRoute>} />
              <Route path="/account/statement" element={<ProtectedRoute><AccountStatement /></ProtectedRoute>} />
              <Route path="/account/invoices" element={<ProtectedRoute><AccountInvoices /></ProtectedRoute>} />
              <Route path="/billdesk" element={<ProtectedRoute><BillDesk /></ProtectedRoute>} />
              <Route path="/account/deals" element={<ProtectedRoute><AccountDeals /></ProtectedRoute>} />
              <Route path="/supplier/license" element={<ProtectedRoute><SupplierLicense /></ProtectedRoute>} />
              <Route path="/supplier/dashboard" element={<ProtectedRoute><SupplierDashboard /></ProtectedRoute>} />
              <Route path="/supplier/elite/apply" element={<ProtectedRoute><SellerEliteApply /></ProtectedRoute>} />
              <Route path="/league" element={<ProtectedRoute><BigCapLeague /></ProtectedRoute>} />
              <Route path="/league/apply" element={<ProtectedRoute><BigCapApply /></ProtectedRoute>} />
              <Route path="/rates-bulletin" element={<RatesBulletin />} />
              <Route path="/account/meetings" element={<ProtectedRoute><AccountMeetings /></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/onboarding/verify" element={<OnboardingVerify />} />
              <Route path="/account/paid-quotes" element={<ProtectedRoute><PaidQuotes /></ProtectedRoute>} />
              <Route path="/supplier/settlement" element={<ProtectedRoute><SupplierSettlement /></ProtectedRoute>} />
              <Route path="/supplier/broadcasts" element={<ProtectedRoute><SupplierBroadcasts /></ProtectedRoute>} />
              <Route path="/verify" element={<ProtectedRoute><KycVerify /></ProtectedRoute>} />
              <Route path="/account/claims" element={<ProtectedRoute><BuyerClaims /></ProtectedRoute>} />
              <Route path="/account/refund-compliance" element={<ProtectedRoute><RefundComplianceDesk /></ProtectedRoute>} />
              <Route path="/account/quotations" element={<ProtectedRoute><SellerQuotations /></ProtectedRoute>} />
              <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
              <Route path="/wallet/activate/:token" element={<WalletActivate />} />
              <Route path="/claim-form/:token" element={<ClaimForm />} />
              <Route path="/director/apply" element={<DirectorApply />} />
              <Route path="/director/kyc" element={<DirectorKYC />} />
              <Route path="/director" element={<DirectorRoute><DirectorPortal /></DirectorRoute>} />
              <Route path="/board-registry" element={<BoardRegistryPage />} />
              <Route path="/board-registry/:seal" element={<BoardRegistryPage />} />
              <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
              <Route path="/manual/*" element={<Navigate to="/" replace />} />
            </Routes>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
